const destinations = [
  {
    name: "Germany",
    context: "Offutt AFB PCS and TDY assignments to Ramstein, Stuttgart, and Wiesbaden; STRATCOM bilateral engagements; corporate travel to Frankfurt and Munich",
    urgency: "PCS orders and STRATCOM TDY often arrive with 14–30 days notice",
    icon: "ri-map-pin-2-line",
    flag: "DE",
  },
  {
    name: "United Kingdom",
    context: "London insurance and reinsurance meetings; Berkshire Hathaway shareholder connections; RAF Mildenhall and Lakenheath STRATCOM assignments; family heritage travel",
    urgency: "Executive meetings and military assignments frequently have 7–14 day lead time",
    icon: "ri-map-pin-2-line",
    flag: "GB",
  },
  {
    name: "Japan & South Korea",
    context: "STRATCOM Pacific theater engagements; Offutt AFB joint assignments at Yokota and Osan; Union Pacific and Kiewit logistics and engineering partnerships",
    urgency: "Deployment and TDY orders have immovable report dates — dependent passports must be current",
    icon: "ri-map-pin-2-line",
    flag: "JP",
  },
  {
    name: "Switzerland & Bermuda",
    context: "Insurance and reinsurance industry meetings in Zurich and Hamilton; financial services conferences; corporate board meetings",
    urgency: "Industry conferences and board meetings have fixed, non-negotiable dates",
    icon: "ri-map-pin-2-line",
    flag: "CH",
  },
  {
    name: "Canada",
    context: "Business travel to Toronto and Vancouver; family vacations to the Canadian Rockies and Winnipeg; conference and industry travel",
    urgency: "Summer vacation and conference bookings peak May through August",
    icon: "ri-map-pin-2-line",
    flag: "CA",
  },
  {
    name: "Italy & Mediterranean",
    context: "Offutt AFB port visits and NATO assignments; Creighton and UNO study-abroad programs; heritage tourism from Omaha's Italian-American community",
    urgency: "Study-abroad deadlines and military assignment windows are fixed and non-negotiable",
    icon: "ri-map-pin-2-line",
    flag: "IT",
  },
];

const commonSituations = [
  {
    stat: "65%",
    label: "of Omaha expedited requests are military-related or corporate/business travel on compressed timelines",
  },
  {
    stat: "2–4 Weeks",
    label: "average notice Omaha travelers have before a planned international trip, STRATCOM TDY, or PCS",
  },
  {
    stat: "Nov–Mar",
    label: "peak season when winter weather makes driving to Minneapolis or Chicago passport agencies hazardous",
  },
  {
    stat: "5 Days",
    label: "shortest turnaround we have accommodated for an Offutt AFB family with emergency PCS orders to Ramstein",
  },
];

export default function OmahaDestinationsSection() {
  return (
    <section className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Travel Intelligence</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Where Omaha Travelers Go — And Why Passports Get Urgent
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            These are the destinations and travel patterns we see most from Eastern Nebraska clients. Understanding local travel behavior is part of why our document review catches issues before they derail your STRATCOM mission, corporate meeting, or family trip.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {commonSituations.map((s, i) => (
            <div key={i} className="bg-[#1A365D] rounded-lg p-6 text-center">
              <p className="font-serif text-3xl font-bold text-[#F6C344] mb-2">{s.stat}</p>
              <p className="text-white/60 text-xs leading-relaxed">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {destinations.map((d, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 group-hover:bg-[#F6C344]/15 transition-colors flex-shrink-0">
                  <i className={`${d.icon} text-[#1A365D] group-hover:text-[#F6C344] transition-colors text-lg`}></i>
                </div>
                <div>
                  <h3 className="font-bold text-[#1A365D] text-sm">{d.name}</h3>
                </div>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed mb-3">
                {d.context}
              </p>
              <div className="flex items-start gap-2">
                <i className="ri-time-line text-[#F6C344] text-xs flex-shrink-0 mt-0.5"></i>
                <p className="text-gray-400 text-xs leading-relaxed italic">
                  {d.urgency}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
              <i className="ri-links-line text-[#1A365D] text-xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-[#1A365D] text-sm mb-1">Explore Other Midwest Service Areas</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                We serve travelers across the Midwest with the same expedited passport processing and document review expertise. Looking for service in another community?
              </p>
            </div>
            <div className="flex flex-wrap gap-2 flex-shrink-0">
              <a href="/des-moines" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Des Moines <i className="ri-arrow-right-line text-xs"></i>
              </a>
              <a href="/kansas-city" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Kansas City <i className="ri-arrow-right-line text-xs"></i>
              </a>
              <a href="/minneapolis" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Minneapolis <i className="ri-arrow-right-line text-xs"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}